/**
 * "Projects the units work on" on the Station health page (decision 788; docs/specs/cloud/diurnal-qc-cloud.md §4): each
 * TA Live unit's links to the projects it works on, with their dates. A manager (or tenant admin) links a unit when it
 * goes to a job and sets the last day when it leaves. One link serves three things: the project's cloud diurnal QC
 * (its base stations), where the unit's alerts go (the project's crew, decision 53), and the time-zone check (the
 * unit's clock against the project's zone). The same links show on each project's Diurnal QC tab.
 */
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { canLinkCloudQcStation, stationLinkDates, stationLinkOn, type StationLinkRow } from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { ErrorState } from '../components/States.tsx';
import { loadTenantLinks, setLink } from '../portal/diurnalApi.ts';

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));

export interface UnitRef {
  readonly id: string;
  readonly name: string;
}

interface ProjectRef {
  readonly id: string;
  readonly number: string;
  readonly title: string;
}

async function loadLiveProjects(sb: SupabaseClient, tenantId: string): Promise<ProjectRef[]> {
  const { data, error } = await sb
    .from('project')
    .select('id, number, title, status')
    .eq('tenant_id', tenantId)
    .is('deleted_at', null)
    .neq('status', 'archived')
    .order('number');
  if (error) throw new Error(error.message);
  return ((data ?? []) as ProjectRef[]).map((p) => ({ id: p.id, number: p.number, title: p.title }));
}

export function StationProjectsPanel({
  sb,
  tenantId,
  roles,
  units,
  today,
}: {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly roles: readonly string[];
  readonly units: readonly UnitRef[];
  /** Today, `YYYY-MM-DD` (the browser's date). */
  readonly today: string;
}): ReactElement | null {
  const mayLink = canLinkCloudQcStation(roles);
  const [links, setLinks] = useState<StationLinkRow[] | null>(null);
  const [projects, setProjects] = useState<ProjectRef[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ unit: '', project: '', starts: today, ends: '' });

  const reload = useCallback(() => {
    Promise.all([loadTenantLinks(sb, tenantId), loadLiveProjects(sb, tenantId)])
      .then(([l, p]) => {
        setLinks(l);
        setProjects(p);
      })
      .catch((e: unknown) => setError(message(e)));
  }, [sb, tenantId]);
  useEffect(reload, [reload]);

  if (units.length === 0) return null;
  const number = (id: string): string => projects.find((p) => p.id === id)?.number ?? 'a project';

  async function change(
    unit: string,
    project: string,
    on: boolean,
    starts: string | null,
    ends: string | null,
  ): Promise<void> {
    setBusy(true);
    setNotice(null);
    try {
      await setLink(sb, project, unit, on, starts, ends);
      const u = units.find((x) => x.id === unit)?.name ?? 'The unit';
      setNotice({
        ok: true,
        text: on
          ? `${u} works on ${number(project)} ${stationLinkDates({ starts_on: starts ?? today, ends_on: ends })}.`
          : `${u} is no longer linked to ${number(project)}.`,
      });
      reload();
    } catch (e) {
      setNotice({ ok: false, text: message(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="panel stack" aria-label="Projects the units work on">
      <h2 className="heading">Projects the units work on</h2>
      <p className="caption muted">
        Link a unit to the project it works on when it goes to a job, and set its last day when it leaves.
        Teslyn Cloud then works out the project’s diurnal QC from the unit, sends the unit’s alerts to the
        project’s field crew, and checks the unit’s clock is on the project’s time zone (its Diurnal QC UTC
        offset). A unit on no project is checked against the zone of the day before.
      </p>
      {error && <ErrorState message={error} />}
      {notice && (
        <p className={notice.ok ? 'notice' : 'notice notice--fail'} role={notice.ok ? 'status' : 'alert'}>
          {notice.text}
        </p>
      )}
      {links !== null && (
        <ul className="stack">
          {units.map((u) => {
            const mine = links.filter((l) => l.station_id === u.id);
            return (
              <li key={u.id} className="row row--wrap" aria-label={`${u.name} projects`}>
                <strong className="body">{u.name}</strong>
                {mine.length === 0 ? (
                  <span className="caption muted">On no project</span>
                ) : (
                  mine.map((l) => (
                    <span key={l.project_id} className="row">
                      <span className={stationLinkOn(l, today) ? 'chip chip--pass' : 'chip'}>
                        {number(l.project_id)} {stationLinkDates(l)}
                      </span>
                      {mayLink && (
                        <Button
                          variant="quiet"
                          disabled={busy}
                          aria-label={`Unlink ${u.name} from ${number(l.project_id)}`}
                          onClick={() => void change(u.id, l.project_id, false, null, null)}
                        >
                          Unlink
                        </Button>
                      )}
                    </span>
                  ))
                )}
              </li>
            );
          })}
        </ul>
      )}
      {mayLink && (
        <div className="row row--wrap" aria-label="Link a unit to a project">
          <label className="row">
            <span className="label">Unit</span>
            <select value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}>
              <option value="">Choose…</option>
              {units.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </label>
          <label className="row">
            <span className="label">Project</span>
            <select value={form.project} onChange={(e) => setForm({ ...form, project: e.target.value })}>
              <option value="">Choose…</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.number} {p.title}
                </option>
              ))}
            </select>
          </label>
          <label className="row">
            <span className="label">From</span>
            <input
              type="date"
              value={form.starts}
              onChange={(e) => setForm({ ...form, starts: e.target.value })}
            />
          </label>
          <label className="row">
            <span className="label">To (optional)</span>
            <input
              type="date"
              value={form.ends}
              onChange={(e) => setForm({ ...form, ends: e.target.value })}
            />
          </label>
          <Button
            variant="primary"
            disabled={busy || !form.unit || !form.project || !form.starts}
            onClick={() => void change(form.unit, form.project, true, form.starts, form.ends || null)}
          >
            Link
          </Button>
        </div>
      )}
    </div>
  );
}
